import { Box, Dialog, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import { Announcement03, Dotpoints01 } from '@untitledui-pro/icons/line';
import React, { useMemo } from 'react';
import { useEnvStore } from 'store/EnvStore';
import { useResourceStore } from 'store/ResourceStore';
import { LoadingStatus, useLoadingStore } from 'store/LoadingStore';
import BackgroundRotator from './generic/BackgroundRotator';
import CustomTooltip from './generic/CustomTooltip';
import DownloadController from './generic/DownloadController';
import ReportIssue from './panels/settings/ReportIssue';
import { useCompactUI } from './compactUi';
import bg1 from '/assets/backgrounds/1.png';
import bg2 from '/assets/backgrounds/2.png';
import bg3 from '/assets/backgrounds/3.png';
import bgFunny from '/assets/backgrounds/funnyBackground.png';

const backgrounds = [bg1, bg2, bg3];
if (import.meta.env.MODE === 'development') {
    // in dev, prefer the funny background to lighten the mood
    backgrounds.push(bgFunny);
}

const CARD_HEIGHT = '25%';
const renderingContentSx = {
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    gap: 0,
};

const LoadingScreen: React.FC<{ drawingStuffs?: boolean }> = ({ drawingStuffs }) => {
    const isCompactUI = useCompactUI();
    const grid = useLoadingStore((state) => state.grid);
    const loadingStatus = useLoadingStore((state) => state.loadingStatus);
    const downloadQueueMap = useLoadingStore((state) => state.downloadQueue);
    const backendUrl = useEnvStore((s) => s.backendUrl);
    const projectId = useResourceStore((s) => s.projectId);
    const [reportIssueOpen, setReportIssueOpen] = React.useState(false);

    const projectPageUrl = useMemo(() => {
        if (!backendUrl) return null;
        return `${backendUrl}/projects/${projectId}`;
    }, [backendUrl, projectId]);
    const handleOpenProjectPage = () => {
        if (!projectPageUrl) return;
        window.open(projectPageUrl, '_blank', 'noreferrer');
    };
    const downloadQueue = React.useMemo(
        () => Object.values(downloadQueueMap).sort((a, b) => a.startedAt - b.startedAt),
        [downloadQueueMap]
    );
    const isDiscoveringFiles = loadingStatus === LoadingStatus.LOADING && downloadQueue.length === 0;

    return (
        <div className="loading-screen">
            <BackgroundRotator images={backgrounds} />
            <div className="loading-screen-content">
                <Paper
                    elevation={0}
                    className="loading-card"
                    sx={{
                        height: CARD_HEIGHT,
                        maxHeight: CARD_HEIGHT,
                    }}
                >
                    {isCompactUI ? (
                        <Box className="loading-card-content" sx={renderingContentSx}>
                            <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
                                Loading...
                            </Typography>
                        </Box>
                    ) : !drawingStuffs ? (
                        <Box className="loading-card-content">
                            <Box className="loading-card-header">
                                <Box className="loading-card-header-text">
                                    <Typography variant="h4" className="loading-title">
                                        {grid ? `Loading ${grid.name}` : 'Preparing data'}
                                    </Typography>
                                    <Typography variant="body1" className="loading-subtitle">
                                        Preparing geodata and simulations for the 3D environment.
                                    </Typography>
                                </Box>
                                <Box className="loading-card-actions loading-card-actions--inline" sx={{ gap: 0.5 }}>
                                    <CustomTooltip side="right" title="report issue">
                                        <IconButton
                                            size="small"
                                            aria-label="Report issue"
                                            className="loading-project-button"
                                            onClick={() => setReportIssueOpen(true)}
                                        >
                                            <Announcement03 className="icon-size-big" />
                                        </IconButton>
                                    </CustomTooltip>
                                    {projectPageUrl ? (
                                        <CustomTooltip side="right" title="open project page">
                                            <IconButton
                                                size="small"
                                                aria-label="Open project page"
                                                className="loading-project-button"
                                                onClick={handleOpenProjectPage}
                                            >
                                                <Dotpoints01 className="icon-size-big" />
                                            </IconButton>
                                        </CustomTooltip>
                                    ) : null}
                                </Box>
                            </Box>
                            <DownloadController queue={downloadQueue} discovering={isDiscoveringFiles} />
                        </Box>
                    ) : (
                        <Box className="loading-card-content" sx={renderingContentSx}>
                            <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
                                Rendering scene...
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </div>
            <Dialog open={reportIssueOpen} onClose={() => setReportIssueOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 11000 }}>
                <DialogContent>
                    <ReportIssue showContent={reportIssueOpen} onFeedbackSent={() => setReportIssueOpen(false)} />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LoadingScreen;
